'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import UILink from 'UIComponents/link/UILink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { UnreviewableReasons } from '../../../constants/Reviewability';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIButton from 'UIComponents/button/UIButton';
import AppReviewability from '../../../records/AppReviewability';
import { getAppShortlink, getConnectedAppsUrl } from '../../../utils/urls';
import ExistingPortalReviewsPanel from './ExistingPortalReviewsPanel';
const textPath = 'appMarketplace.detail.reviews';

const WriteReviewButton = ({
  className,
  disabled,
  offeringId,
  onClick,
  reviewability,
  size,
  tooltipPlacement,
  use
}) => {
  const {
    pathname
  } = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  const isReviewable = reviewability.isReviewable();
  const reason = reviewability.getUnreviewableReason();
  const tooltipMessage = useMemo(() => {
    const reasonTextPath = `${textPath}.write.unreviewable.${reason}`;

    switch (reason) {
      case UnreviewableReasons.NOT_INSTALLED:
        {
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `${reasonTextPath}_jsx`,
            options: {
              external: true,
              href: getConnectedAppsUrl(true)
            },
            elements: {
              Link: UILink
            }
          });
        }

      case UnreviewableReasons.UNINSTALLED:
        {
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `${reasonTextPath}_jsx`,
            elements: {
              UninstallWindow: /*#__PURE__*/_jsx(FormattedDuration, {
                from: 1,
                to: reviewability.get('maxMillisAfterUninstall')
              })
            }
          });
        }

      case UnreviewableReasons.LIMIT_REACHED:
        {
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `${reasonTextPath}_jsx`,
            options: {
              onClick: () => setPanelOpen(true)
            },
            elements: {
              Link: UILink
            }
          });
        }

      default:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: reasonTextPath
        });
    }
  }, [reviewability, reason]);

  if (reason === UnreviewableReasons.PUBLIC_USER) {
    return /*#__PURE__*/_jsx(UIButton, {
      className: className,
      href: getAppShortlink(`${pathname}/write-review`),
      use: use,
      size: size,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.write.button`
      })
    });
  }

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UITooltip, {
      title: tooltipMessage,
      disabled: isReviewable,
      placement: tooltipPlacement,
      children: /*#__PURE__*/_jsx(UIButton, {
        className: className,
        onClick: onClick,
        use: use,
        size: size,
        disabled: disabled || !isReviewable,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.write.button`
        })
      })
    }), panelOpen && /*#__PURE__*/_jsx(ExistingPortalReviewsPanel, {
      offeringId: offeringId,
      onClose: () => setPanelOpen(false),
      reviewability: reviewability
    })]
  });
};

WriteReviewButton.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  offeringId: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  reviewability: PropTypes.instanceOf(AppReviewability).isRequired,
  size: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  use: PropTypes.string
};
WriteReviewButton.defaultProps = {
  disabled: false,
  size: 'sm',
  tooltipPlacement: 'left',
  use: 'tertiary'
};
export default WriteReviewButton;